"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SPQuarry = void 0;
const tslib_1 = require("tslib");
const quarry_sdk_1 = require("@quarryprotocol/quarry-sdk");
const solana_contrib_1 = require("@saberhq/solana-contrib");
const token_utils_1 = require("@saberhq/token-utils");
const spl_token_1 = require("@solana/spl-token");
const web3_js_1 = require("@solana/web3.js");
const pda_1 = require("./pda");
const vault_1 = require("./vault");
class SPQuarry {
    constructor(sdk) {
        this.sdk = sdk;
    }
    get programs() {
        return this.sdk.programs;
    }
    get program() {
        return this.programs.SunnyPoolQuarry;
    }
    get provider() {
        return this.sdk.provider;
    }
    /**
     * Creates a new pool.
     * @returns
     */
    newPool({ creator = this.provider.wallet.publicKey, quarry, payer = this.provider.wallet.publicKey, internalMintKP = web3_js_1.Keypair.generate(), }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const [quarryRaw] = yield Promise.all([
                this.provider.getAccountInfo(quarry),
            ]);
            if (!quarryRaw) {
                throw new Error(`Could not find quarry: ${quarry.toString()}`);
            }
            const parsedQuarry = this.sdk.quarrySDK.programs.Mine.coder.accounts.decode("Quarry", quarryRaw.accountInfo.data);
            const rewarder = parsedQuarry.rewarderKey;
            const vendorMint = parsedQuarry.tokenMintKey;
            const vendorMintDecimals = parsedQuarry.tokenMintDecimals;
            const rewarderRaw = yield this.provider.getAccountInfo(rewarder);
            if (!rewarderRaw) {
                throw new Error(`Could not find rewarder: ${rewarder.toString()}`);
            }
            const parsedRewarder = this.sdk.quarrySDK.programs.Mine.coder.accounts.decode("Rewarder", rewarderRaw.accountInfo.data);
            const rewardsMint = parsedRewarder.rewardsTokenMint;
            const [pool, bump] = yield (0, pda_1.findPoolAddress)({
                programId: this.program.programId,
                creator,
                quarry,
            });
            const { instructions: createAccountInstructions } = yield (0, token_utils_1.getOrCreateATAs)({
                provider: this.provider,
                mints: {
                    rewards: rewardsMint,
                    vendor: vendorMint,
                },
                owner: pool,
            });
            const createInternalMint = yield (0, token_utils_1.createInitMintInstructions)({
                provider: this.provider,
                mintKP: internalMintKP,
                decimals: vendorMintDecimals,
                mintAuthority: pool,
                freezeAuthority: pool,
            });
            const newPoolIx = this.program.instruction.newPool(bump, {
                accounts: {
                    creator,
                    rewarder,
                    quarry,
                    pool,
                    payer,
                    systemProgram: web3_js_1.SystemProgram.programId,
                    internalMint: internalMintKP.publicKey,
                },
            });
            const createPool = new solana_contrib_1.TransactionEnvelope(this.provider, [
                ...createAccountInstructions,
                newPoolIx,
            ]);
            return {
                key: pool,
                tx: createInternalMint.combine(createPool),
                internalToken: token_utils_1.Token.fromMint(internalMintKP.publicKey, vendorMintDecimals),
            };
        });
    }
    updateClaimFee({ pool, fee, admin = this.provider.wallet.publicKey, }) {
        const megaBPS = new token_utils_1.u64(fee.asFraction
            // convert [0,1] fraction into bps
            .multiply(10000)
            // convert bps into megabps
            .multiply(1000000)
            .quotient.toString());
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            this.program.instruction.updateClaimFee({ megaBps: megaBPS }, {
                accounts: {
                    admin,
                    pool,
                },
            }),
        ]);
    }
    fetchPoolData(key) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            return {
                key,
                data: yield this.program.account.pool.fetch(key),
            };
        });
    }
    fetchVaultData(key) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            return {
                key,
                data: yield this.program.account.vault.fetch(key),
            };
        });
    }
    findPoolAddress({ creator = this.sdk.environment.creator, quarry, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const [pool] = yield (0, pda_1.findPoolAddress)({
                programId: this.program.programId,
                creator,
                quarry,
            });
            return pool;
        });
    }
    findVaultAddress({ owner = this.provider.wallet.publicKey, pool, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const [vault] = yield (0, pda_1.findVaultAddress)({
                programId: this.program.programId,
                pool,
                owner,
            });
            return vault;
        });
    }
    /**
     * Creates a new Vault.
     * @param param0
     * @returns
     */
    newVault({ owner = this.provider.wallet.publicKey, payer = this.provider.wallet.publicKey, pool: { key: poolKey, data: { rewarder, internalMint, vendorMint }, }, rewardsMint, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const [vault, bump] = yield (0, pda_1.findVaultAddress)({
                programId: this.program.programId,
                pool: poolKey,
                owner,
            });
            // vault ATAs
            const { instructions } = yield (0, token_utils_1.getOrCreateATAs)({
                provider: this.provider,
                mints: {
                    rewards: rewardsMint,
                    internal: internalMint,
                    vendor: vendorMint,
                },
                owner: vault,
            });
            const newVaultIX = this.program.instruction.initVault(bump, {
                accounts: {
                    pool: poolKey,
                    owner,
                    vault,
                    payer,
                    systemProgram: web3_js_1.SystemProgram.programId,
                },
            });
            const { tx: initVendorMinerTX } = yield this.initMiner({
                mint: vendorMint,
                pool: poolKey,
                vault,
                payer,
                rewarder,
            });
            const { tx: initInternalMinerTX } = yield this.initMiner({
                mint: internalMint,
                pool: poolKey,
                vault,
                payer,
                rewarder: this.sdk.environment.rewarder,
            });
            return {
                key: vault,
                tx: new solana_contrib_1.TransactionEnvelope(this.provider, [...instructions, newVaultIX])
                    .combine(initVendorMinerTX)
                    .combine(initInternalMinerTX),
            };
        });
    }
    initMiner({ mint, pool, vault, payer = this.provider.wallet.publicKey, rewarder, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const [quarryKey] = yield (0, quarry_sdk_1.findQuarryAddress)(rewarder, mint);
            const [minerKey, minerBump] = yield (0, quarry_sdk_1.findMinerAddress)(quarryKey, vault);
            const minerATA = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: mint,
                owner: minerKey,
            });
            const initMinerIX = this.program.instruction.initMiner(minerBump, {
                accounts: {
                    mineProgram: this.sdk.quarrySDK.mine.program.programId,
                    pool,
                    vault,
                    systemProgram: web3_js_1.SystemProgram.programId,
                    payer,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    rewarder,
                    miner: minerKey,
                    quarry: quarryKey,
                    tokenMint: mint,
                    minerVault: minerATA.address,
                },
            });
            return {
                tx: new solana_contrib_1.TransactionEnvelope(this.provider, [
                    ...(minerATA.instruction ? [minerATA.instruction] : []),
                    initMinerIX,
                ]),
                miner: minerKey,
            };
        });
    }
    /**
     * Loads a vault.
     * @returns
     */
    loadVault({ vaultKey }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const vault = yield this.fetchVaultData(vaultKey);
            const pool = yield this.fetchPoolData(vault.data.pool);
            return new vault_1.Vault(this, pool, vault);
        });
    }
}
exports.SPQuarry = SPQuarry;
//# sourceMappingURL=sunnyPoolQuarry.js.map